/**
 * Fly's chat (Crisp) on the agent pages (Anand, 18 Sep 26): the floating
 * launcher stays retracted, because it sat on top of the approve sheet and
 * the card buttons, and the chat opens from the burger menu instead. Crisp
 * queues commands pushed before it loads, so these are safe to call early.
 *
 * The signed-in app (WO-69, Anand, 13 Oct 26: "Support chat, add widget."):
 * the account, workspace and location are named for support
 * (src/lib/appSupportChat.ts), and the launcher sits under every sheet and
 * dialog (src/styles/agent-app-support-chat.css). Where it shows there:
 *
 *   - on a phone, a location's pages tuck it: the tab bar's Chat button
 *     holds both chats (agent-shell/ChatFan.tsx, Shardul, 14 Oct 26); the
 *     account pages show it over their tab bar;
 *   - from a tablet up it is tucked at all times (Anand, 15 Oct 26: "There
 *     are two chat icons on desktop one above the other, they are not
 *     looking good, place the support chat elsewhere pls."). The Ask the
 *     agent button keeps the bottom right corner alone, and Fly support
 *     opens from "Chat with Fly support" in the sidebar, the rail and the
 *     Menu (agent-shell/SupportChatEntry.tsx), which carries the count of
 *     unread replies so an answer from support is not missed.
 *
 * The shared links (/agent/...) keep the launcher tucked away as before.
 */

import { parseAppLocationPath } from './appAddress';
import { isAgencyMode } from '../utils/agencyRouting';

type CrispQueue = {
  push: (cmd: unknown[]) => void;
  /** Crisp's running client only (the loader's queue has neither). */
  get?: (key: string) => unknown;
  is?: (key: string) => unknown;
};
const crisp = (): CrispQueue | null => {
  if (typeof window === 'undefined') return null;
  const c = (window as unknown as { $crisp?: CrispQueue }).$crisp;
  return c && typeof c.push === 'function' ? c : null;
};

/** The signed-in app: /app and every page under it (the account area and /app/l/<location>/...). */
export const onAppPage = (pathname: string): boolean => pathname === '/app' || pathname.startsWith('/app/');

/**
 * A location's pages in the app (/app/l/<location>/..., and /app/<slug>/... on Fly's own hosts, the only
 * place the clean address exists): the frame whose phone tab bar has the Chat fan.
 */
export const onAppLocationPage = (pathname: string): boolean => {
  const at = parseAppLocationPath(pathname);
  return at !== null && (at.shape === 'id' || !isAgencyMode());
};

/**
 * Where the launcher stays tucked away, for the page and the screen width
 * (`phone`: under 768px, the tab bar's breakpoint; read from the screen when
 * not given):
 *   - the shared links (/agent/...), always: their own floating "Ask the
 *     agent" button takes that corner (owner, 29 Sep 26), and support chat
 *     stays one tap away in the Menu (openFlyChat);
 *   - the signed-in app from a tablet up, always (Anand, 15 Oct 26): Fly
 *     support is in the sidebar, the rail and the Menu;
 *   - a location's pages in the app on a phone: the tab bar's Chat fan holds
 *     it (Shardul, 14 Oct 26);
 *   - the account pages on a phone show it, over the tab bar (WO-69);
 *   - every other page shows it.
 */
export function tucksChatLauncher(pathname: string, phone: boolean = phoneWidth()): boolean {
  if (pathname.startsWith('/agent/')) return true;
  if (!onAppPage(pathname)) return false;
  return !phone || onAppLocationPage(pathname);
}

/**
 * A customer's page of a business, not a Fly user's: the booking calendar
 * (/book/<slug>), a booking's change link (/booking/<token>) and the private
 * feedback page (/f/<token>). Fly's support chat never loads on them: the
 * visitor is the business's customer, and on the booking page the Crisp
 * runtime also threw "Invalid data" three times a visit (Anand, 10 Oct 26).
 */
export const onCustomerPage = (pathname: string): boolean => /^\/(book|booking|f)\/[^/]+/.test(pathname);

export function hideFlyChatLauncher(): void {
  crisp()?.push(['do', 'chat:hide']);
}
export function showFlyChatLauncher(): void {
  crisp()?.push(['do', 'chat:show']);
}

export const PHONE_QUERY = '(max-width: 767px)';
/** True on a phone-width screen (the tab bar's own breakpoint). False where matchMedia is missing. */
export function phoneWidth(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(PHONE_QUERY).matches;
}

/** True while Crisp's chat window is open. Only its running client can say; the loader's queue answers false. */
function chatOpen(): boolean {
  const c = crisp();
  if (!c || typeof c.is !== 'function') return false;
  try {
    return c.is('chat:opened') === true;
  } catch {
    return false;
  }
}

/**
 * Puts the launcher where the rule says for the page on screen
 * (tucksChatLauncher). An open chat is never tucked here: Crisp's chat:hide
 * "sets it invisible" even while it is open, and a page change in the middle
 * of a conversation with support must not make the window vanish. It tucks
 * when the visitor closes it (bindFlyChatEvents).
 */
export function applyFlyChatRule(pathname?: string): void {
  if (typeof window === 'undefined') return;
  const path = pathname ?? window.location.pathname;
  if (tucksChatLauncher(path)) {
    if (!chatOpen()) hideFlyChatLauncher();
  } else {
    showFlyChatLauncher();
  }
}

/**
 * While a page of the signed-in app is up (useAppSupportChat, the phone
 * tab bar's Chat tab): the rule now, and again whenever the screen crosses
 * the phone width (a window resized, a tablet turned), with Crisp's events
 * bound. `pathname` is the router's, when the caller has it (else the
 * address bar's). Returns the function that stops listening for the width.
 */
export function followFlyChatRule(pathname?: string): () => void {
  applyFlyChatRule(pathname);
  bindFlyChatEvents();
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => undefined;
  const mq = window.matchMedia(PHONE_QUERY);
  const onChange = () => applyFlyChatRule(pathname);
  mq.addEventListener?.('change', onChange);
  return () => mq.removeEventListener?.('change', onChange);
}

/** Opens the chat from a menu item. False when Crisp is not on the page. */
export function openFlyChat(): boolean {
  const c = crisp();
  if (!c) return false;
  c.push(['do', 'chat:show']);
  c.push(['do', 'chat:open']);
  return true;
}

/* -------------------------------------------------------- Crisp's events */

let boundTo: CrispQueue | null = null;

/**
 * Crisp's events, bound through this one place. Crisp keeps one callback per
 * event: its own SDK (crisp-sdk-web) pushes "off" before every "on", and
 * "off" names only the event. So nothing else binds an event of its own:
 *   chat:closed        the launcher goes back where the rule says (tucked on
 *                      a shared link and, in the app, from a tablet up and on
 *                      a location's pages on a phone), and the unread count
 *                      is read again;
 *   chat:opened, message:received, session:loaded
 *                      the unread count is read again.
 * Safe to call early (Crisp runs queued commands when it loads) and often:
 * it binds again only when the page's $crisp is a new one (Crisp's client
 * takes the loader's queue's place), which replaces the same callbacks.
 */
export function bindFlyChatEvents(): void {
  const c = crisp();
  if (!c || c === boundTo) return;
  boundTo = c;
  c.push([
    'on',
    'chat:closed',
    () => {
      if (typeof window !== 'undefined' && tucksChatLauncher(window.location.pathname)) hideFlyChatLauncher();
      refreshSupportUnread();
    },
  ]);
  c.push(['on', 'chat:opened', () => refreshSupportUnread()]);
  c.push(['on', 'message:received', () => refreshSupportUnread()]);
  c.push(['on', 'session:loaded', () => refreshSupportUnread()]);
}

/* ------------------------------------------------ unread support replies */

/** How long after an event the unread count is read a second time. */
export const UNREAD_RECHECK_MS = 1000;

let unread = 0;
const unreadListeners = new Set<() => void>();

/** Crisp's count of unread messages in the chat ("chat:unread:count"); 0 until its client runs. */
function readUnread(): number {
  const c = crisp();
  if (!c || typeof c.get !== 'function') return 0;
  try {
    const n = c.get('chat:unread:count');
    return typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  } catch (err) {
    console.warn('[flyChat] could not read the unread count from Crisp', err);
    return 0;
  }
}

function setUnread(n: number): void {
  if (n === unread) return;
  unread = n;
  console.log('[flyChat] unread support messages', { unread: n });
  for (const fn of [...unreadListeners]) fn();
}

/**
 * Reads Crisp's unread count now, and once more a moment later: Crisp's
 * docs do not say whether the count already includes a reply that just came
 * in (or has dropped for a chat just opened) when its callback runs.
 */
export function refreshSupportUnread(): void {
  setUnread(readUnread());
  if (typeof window !== 'undefined') window.setTimeout(() => setUnread(readUnread()), UNREAD_RECHECK_MS);
}

/** Unread replies from Fly support, as Crisp last said. */
export function getSupportUnread(): number {
  return unread;
}

/** Hear every change of the unread count, for useSyncExternalStore. */
export function subscribeSupportUnread(fn: () => void): () => void {
  unreadListeners.add(fn);
  return () => {
    unreadListeners.delete(fn);
  };
}
